// Sawyer 12 incident-wording-pet-notes (F3) — client for
// `GET/PUT /api/v2/admin/tenant/vocabulary`.
//
// The READ runs on every admin page (AppShell labels the nav), so it is
// deliberately paranoid (refuter C5):
//   - a RAW fetch, never `request<T>`: a 401 must never sign a working user
//     out over a display word, and a pre-deploy server answers 403
//     `route_not_mapped` until the server deploy lands;
//   - the 200 body is zod-parsed; `{}` from a catch-all test stub, a wrong
//     shape or non-JSON resolves to an error result, never a throw;
//   - it never logs, so console-silent specs elsewhere stay silent.
// The WRITE is the owner's Save in Settings and goes through `api.put`
// (the house path; a 401 there is a real expired session).
import { z } from 'zod';
import { api, type ApiResponse } from './api.js';
import type { IncidentLabel, VocabularySource } from './tenant-vocabulary.js';

const wordSchema = z.string().min(1).max(60);

export const tenantVocabularyViewSchema = z.object({
  incidentLabel: z.object({ singular: wordSchema, plural: wordSchema }),
  source: z.object({ incidentLabel: z.enum(['configured', 'default']) }),
  limits: z.object({ maxWordLength: z.number() }).optional(),
  lastChange: z.object({ at: z.string(), byName: z.string() }).nullable().optional(),
});

export interface TenantVocabularyView {
  incidentLabel: IncidentLabel;
  source: { incidentLabel: VocabularySource };
  limits?: { maxWordLength: number };
  lastChange?: { at: string; byName: string } | null;
}

export interface TenantVocabularyPatch {
  incidentLabel: IncidentLabel | null;
}

export type TenantVocabularyUpdateResponse = TenantVocabularyView & { changed: boolean; audited: boolean };

export type TenantVocabularyFetch =
  | { kind: 'ok'; view: TenantVocabularyView }
  /** 403 route_not_mapped: this server predates the route (deploy window). */
  | { kind: 'not_deployed' }
  /** Network, 5xx, 503 retryable, 401/403/404, malformed body. */
  | { kind: 'error' };

/** Parses a 200 body; null on any mismatch. Never throws. */
export function parseTenantVocabularyView(body: unknown): TenantVocabularyView | null {
  try {
    const parsed = tenantVocabularyViewSchema.safeParse(body);
    return parsed.success ? (parsed.data as TenantVocabularyView) : null;
  } catch {
    return null;
  }
}

export async function fetchTenantVocabulary(apiUrl: string, token: string | null): Promise<TenantVocabularyFetch> {
  try {
    const res = await fetch(`${apiUrl}/api/v2/admin/tenant/vocabulary`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (res.status === 200) {
      const view = parseTenantVocabularyView(body);
      return view ? { kind: 'ok', view } : { kind: 'error' };
    }
    if (res.status === 403 && body !== null && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      if (b['reason'] === 'route_not_mapped' || b['error'] === 'route_not_mapped') return { kind: 'not_deployed' };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export const adminTenantVocabularyApi = {
  update: (apiUrl: string, patch: TenantVocabularyPatch): Promise<ApiResponse<TenantVocabularyUpdateResponse>> =>
    api.put<TenantVocabularyUpdateResponse>(apiUrl, '/api/v2/admin/tenant/vocabulary', patch),
};
